import {QueryClient} from '@tanstack/react-query';
import axios, {AxiosRequestConfig} from 'axios';
import {getActiveWorkspaceId} from '../workspace/active-workspace-id';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';
import {errorStatusIs} from '@common/http/error-status-is';
import {getEchoSocketId} from '@common/http/get-echo-socket-id';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

// SSR YARIŞ DURUMU: eşzamanlı render'lar tek önbelleği paylaşırsa diller
// birbirine karışır (kanal önbellek anahtarı ['channel', id, params] — dil YOK,
// yani /tr/ ile /en/ aynı girdiye düşer) ve biten isteğin clear() çağrısı uçuştaki
// render'ın verisini siler. Bu yüzden Node girişi istek başına kendi istemcisini
// kurar. Fabrika ayrıldı; aşağıdaki tekil export aynen korunuyor → bu modülü
// import eden 243 dosya etkilenmez, tarayıcı davranışı birebir aynı.
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30000,
        retry: (failureCount, err) => {
          return (
            !errorStatusIs(err, 401) &&
            !errorStatusIs(err, 403) &&
            !errorStatusIs(err, 404) &&
            failureCount < 2
          );
        },
      },
    },
  });
}

export const queryClient = createQueryClient();

export const apiClient = axios.create();
apiClient.defaults.withCredentials = true;
apiClient.defaults.responseType = 'json';
// @ts-ignore
apiClient.defaults.headers = {
  common: {
    Accept: 'application/json',
  },
};

const internalEndpoints = ['auth', 'secure', 'log-viewer', 'horizon'];
// @ts-ignore
apiClient.interceptors.request.use((config: AxiosRequestConfig) => {
  if (
    !internalEndpoints.some(endpoint => config.url?.startsWith(endpoint)) &&
    !isAbsoluteUrl(config?.url)
  ) {
    config.url = `api/v1/${config.url}`;
  }

  const method = config.method?.toUpperCase();

  // B20 (Day 29): forward the active locale to the API so SEO meta (and any
  // locale-aware payload) is rendered in the right language during client-side
  // SPA navigation. SetLocale skips api/*, but Common\SetAppLocale honours the
  // `lang` query param first. Without this the API falls back to the default
  // locale (tr) and detail-page <title>/<meta> would leak Turkish on /en, etc.
  // Only added for GET (read) requests and never overrides an explicit value.
  if (method === 'GET' && config.params?.lang == null) {
    const lang = getBootstrapData()?.i18n?.language;
    if (lang) {
      config.params = {...config.params, lang};
    }
  }

  // transform array query params in GET request to comma separated string
  if (method === 'GET' && Array.isArray(config.params?.with)) {
    config.params.with = config.params.with.join(',');
  }
  if (method === 'GET' && Array.isArray(config.params?.load)) {
    config.params.load = config.params.load.join(',');
  }
  if (method === 'GET' && Array.isArray(config.params?.loadCount)) {
    config.params.loadCount = config.params.loadCount.join(',');
  }

  // add workspace query parameter
  const workspaceId = getActiveWorkspaceId();
  if (workspaceId) {
    const method = config.method?.toLowerCase();
    if (
      ['get', 'post', 'put'].includes(method!) &&
      config.params?.workspaceId == null
    ) {
      config.params = {...config.params, workspaceId};
    }
  }

  const echoSocketId = getEchoSocketId();
  if (echoSocketId) {
    config.headers = {
      ...config.headers,
      // @ts-ignore
      'X-Socket-ID': echoSocketId,
    };
  }

  // override PUT, DELETE, PATCH methods, they might not be supported on the backend
  if (method === 'PUT' || method === 'DELETE' || method === 'PATCH') {
    config.headers = {
      ...config.headers,
      'X-HTTP-Method-Override': method,
    };
    config.method = 'POST';
    config.params = {
      ...config.params,
      _method: method,
    };
  }

  if (import.meta.env.SSR) {
    config.headers = {
      ...config.headers,
      referer: 'http://localhost',
    };
  }

  return config;
});
